import { Suspense, lazy, useEffect, useState } from 'react';
import { BrandArtwork } from '@/components/brand/BrandArtwork';

/**
 * Lazily-loaded 3D hero — BUILD-PLAN §28 (performance), §40/§47 (motion),
 * §41 (every state handled), §74 (never a broken visual).
 *
 * three.js is roughly 600 kB. Shipping it in the initial bundle would delay
 * first paint on the most important page on the site, so it is code-split and
 * only fetched after mount. Until it arrives — and if it never arrives — the
 * SVG artwork stands in, so the hero is complete at every moment rather than
 * showing a hole.
 *
 * The 3D scene is skipped entirely for:
 *   • prefers-reduced-motion — an idly rotating object is exactly the kind of
 *     vestibular trigger that setting exists for
 *   • devices reporting few CPU cores, where WebGL would cost more in jank
 *     than it returns in impression
 */

const LuxeGiftBox = lazy(() => import('@/components/three/LuxeGiftBox'));

interface Hero3DProps {
  /** Seed for the fallback artwork, so it matches the rest of the page. */
  seed: string;
  className?: string;
}

export function Hero3D({ seed, className = '' }: Hero3DProps) {
  const [mode, setMode] = useState<'pending' | '3d' | 'static'>('pending');
  const [autoRotate, setAutoRotate] = useState(true);

  useEffect(() => {
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    /*
     * Capability, not guesswork.
     *
     * This previously skipped 3D whenever hardwareConcurrency was 4 or fewer,
     * which silently disabled the scene on plenty of perfectly capable
     * machines — many browsers under-report cores, and some clamp the value
     * for fingerprinting resistance. The only question that actually matters
     * is whether WebGL is available, so ask that directly and keep the core
     * count as a floor for genuinely tiny devices.
     */
    const canvas = document.createElement('canvas');
    const hasWebGL = Boolean(
      canvas.getContext('webgl2') ?? canvas.getContext('webgl'),
    );

    const veryLowPower = (navigator.hardwareConcurrency ?? 8) <= 2;

    /*
     * Reduced motion suppresses the *idle spin*, not the 3D itself.
     *
     * WCAG 2.3.3 is about motion the user did not ask for. Dragging an object
     * to turn it is direct manipulation — the movement is the user's own
     * input, and removing it would take away a feature rather than an
     * animation. Previously this disabled the scene entirely, which meant
     * anyone with the OS setting on (common on Windows) never saw the 3D at
     * all.
     */
    setAutoRotate(!reducedMotion);

    setMode(veryLowPower || !hasWebGL ? 'static' : '3d');
  }, []);

  const fallback = <BrandArtwork seed={seed} motif="parcel" ratio="portrait" />;

  if (mode !== '3d') {
    return <div className={className}>{fallback}</div>;
  }

  return (
    <div className={`relative ${className}`}>
      {/* The artwork sits underneath permanently: the canvas has a
          transparent background, so this reads as the scene's backdrop and
          also covers the moment before the chunk resolves. */}
      <div className="absolute inset-0">{fallback}</div>

      <Suspense fallback={null}>
        <div className="absolute inset-0">
          <LuxeGiftBox autoRotate={autoRotate} />
        </div>
      </Suspense>

      {/* Affordance — without it, most visitors never discover the box turns. */}
      <span className="pointer-events-none absolute inset-x-0 bottom-4 text-center text-[0.5625rem] uppercase tracking-[0.28em] text-ink/45">
        Drag to turn
      </span>
    </div>
  );
}
